import { toCsv } from './csvText';
import { SEND_STATUS_LABELS } from './reportStatus';
import { MISSING_TAG_LABELS } from './missingBills';
import { COVERAGE_LABELS, type BillRunRow } from './billRunRows';
import {
  ASSIGNED_LABELS, BILL_RUN_DETAIL_LABELS, BILL_RUN_STATUS_LABELS, billRunStatus, type BillRunStatus,
} from './billRunStatus';

// What a Bill Runs row shows, as plain values. The table and the CSV export both
// read this, so they cannot disagree.

export type BillRunDisplay = {
  key: string;
  kind: BillRunRow['kind'];
  billId: string | null;
  siteId: string;
  clientId: string | null;
  nmi: string;
  entity: string;
  retailer: string;
  tariff: string;
  /** `YYYY-MM`. */
  month: string;
  periodFrom: string | null;
  periodTo: string | null;
  expectedKwh: number | null;
  expected: number | null;
  actual: number | null;
  variance: number | null;
  status: BillRunStatus;
  statusLabel: string;
  detailLabel: string;
  /** Bill rows: the person-set step. Missing rows: the missing-month tag. */
  assignedLabel: string;
  sendLabel: string;
  coverageLabel: string;
  /** Missing rows only: "Covered by" period, or the tag's note. */
  note: string;
  /** Missing rows only: the site's latest bill end date, "Never" when it has had none; '' on bill rows. */
  lastBill: string;
};

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function displayRow(row: BillRunRow): BillRunDisplay {
  const status = billRunStatus(row);
  const common = {
    key: row.key,
    kind: row.kind,
    siteId: row.siteId,
    month: row.month,
    status,
    statusLabel: BILL_RUN_STATUS_LABELS[status.status],
    detailLabel: status.details ? BILL_RUN_DETAIL_LABELS[status.details] : '',
  };

  if (row.kind === 'missing') {
    const site = row.site;
    const extn = (site.bv_extn ?? {}) as Record<string, unknown>;
    return {
      ...common,
      billId: null,
      clientId: null,
      nmi: row.nmi,
      entity: text(site.client?.name),
      retailer: text(site.current_retailer),
      tariff: text(extn.network_tariff_code),
      periodFrom: null,
      periodTo: null,
      expectedKwh: null,
      expected: null,
      actual: null,
      variance: null,
      assignedLabel: row.tag ? MISSING_TAG_LABELS[row.tag.tag] : '',
      sendLabel: '',
      coverageLabel: COVERAGE_LABELS[row.coverage],
      note: row.coverage === 'covered' && row.coveredBy
        ? `Covered by ${row.coveredBy.from ?? '?'} – ${row.coveredBy.to ?? '?'}`
        : row.tag?.note ?? '',
      lastBill: row.lastBill ?? 'Never',
    };
  }

  const bill = row.board.bill;
  const site = bill.site;
  const extn = (site?.bv_extn ?? {}) as Record<string, unknown>;
  return {
    ...common,
    billId: bill.id,
    clientId: (site as { client_id?: string | null } | null | undefined)?.client_id ?? null,
    nmi: text(site?.nmi),
    entity: text(site?.client?.name),
    retailer: text(site?.current_retailer),
    tariff: text(extn.network_tariff_code),
    periodFrom: bill.bill_from ?? null,
    periodTo: bill.bill_to ?? null,
    expectedKwh: typeof bill.total_kwh === 'number' ? bill.total_kwh : null,
    expected: row.figures?.expected ?? null,
    actual: row.figures?.actual ?? null,
    variance: row.figures?.variance ?? null,
    assignedLabel: status.assigned ? ASSIGNED_LABELS[status.assigned] : '',
    sendLabel: SEND_STATUS_LABELS[status.send],
    coverageLabel: COVERAGE_LABELS[row.coverage],
    note: '',
    lastBill: '',
  };
}

const money = (n: number | null) => (n == null ? '' : n.toFixed(2));

export const BILL_RUNS_CSV_HEADERS = [
  'Month', 'NMI', 'Entity', 'Retailer', 'Tariff', 'Period from', 'Period to', 'Expected total kWh',
  'Expected total', 'Actual total', 'Variance', 'Status', 'Status details', 'Assigned status',
  'Send status', 'Coverage', 'Note', 'Last bill',
] as const;

type BillRunsHeader = (typeof BILL_RUNS_CSV_HEADERS)[number];
type BillRunsCell = string | number | null;

/** One row's export values by column; figures stay numbers (null when absent). */
function exportValues(d: BillRunDisplay): Record<BillRunsHeader, BillRunsCell> {
  return {
    Month: d.month,
    NMI: d.nmi,
    Entity: d.entity,
    Retailer: d.retailer,
    Tariff: d.tariff,
    'Period from': d.periodFrom ?? '',
    'Period to': d.periodTo ?? '',
    'Expected total kWh': d.expectedKwh,
    'Expected total': d.expected,
    'Actual total': d.actual,
    Variance: d.variance,
    Status: d.statusLabel,
    'Status details': d.detailLabel,
    'Assigned status': d.assignedLabel,
    'Send status': d.sendLabel,
    Coverage: d.coverageLabel,
    Note: d.note,
    'Last bill': d.lastBill,
  };
}

const MONEY_COLUMNS: ReadonlySet<BillRunsHeader> = new Set<BillRunsHeader>(['Expected total', 'Actual total', 'Variance']);

/** Every row as CSV, one line per row, in the order given. */
export function billRunsCsv(rows: readonly BillRunRow[]): string {
  return toCsv(BILL_RUNS_CSV_HEADERS, rows.map((r) => {
    const v = exportValues(displayRow(r));
    const line: Record<string, string> = {};
    for (const h of BILL_RUNS_CSV_HEADERS) {
      const x = v[h];
      line[h] = typeof x === 'number' ? (MONEY_COLUMNS.has(h) ? money(x) : String(x)) : x ?? '';
    }
    return line;
  }));
}

/**
 * The CSV's rows and columns for an XLSX sheet: figures stay numbers (money
 * rounded to cents) so the spreadsheet can sum them; blanks stay blank.
 */
export function billRunsSheetRows(rows: readonly BillRunRow[]): Record<string, string | number>[] {
  return rows.map((r) => {
    const v = exportValues(displayRow(r));
    const line: Record<string, string | number> = {};
    for (const h of BILL_RUNS_CSV_HEADERS) {
      const x = v[h];
      line[h] = x == null ? '' : typeof x === 'number' && MONEY_COLUMNS.has(h) ? Math.round(x * 100) / 100 : x;
    }
    return line;
  });
}
